import './index.scss';
import React, { useContext, useRef, useState } from 'react';
import { ColumnSchema, ResultTableType } from '@/model/Table';
import TaskApi, { ExecutionStatus, TaskResponse } from '@/api/TaskApi';
import axios, { AxiosResponse } from 'axios';
import { castExecutionResultsToQueryResult } from '@/utils/adapters/DataCloudAdapater';
import Request from '@/api/Request';
import { SQLFile } from '@/utils/db/dataCloud/sqlFileDB';
import { BlockLoading } from '@/components/Loading/BlockLoading';
import { useTranslation } from 'react-i18next';
import { Button, Form, Progress, Row } from '@douyinfe/semi-ui';
import { Icon, Modal, Toast } from '@/components';
import { ExcelUtils } from '@/utils';
import classNames from 'classnames';
import { APIGeneratorContext } from '@/views/dataCloudStudio/components/Providers/components/APIGeneratorProvider';
import useApiKey from '@/utils/hooks/dataCloud/useApiKey';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { FeatureName, useWhiteList } from '@/hooks/UseWhiteList';
import QueryStatus from './components/QueryStatus';
import QueryResultEmpty from './components/QueryResultEmpty';
import QueryResultTable from './components/QueryResultTable';
import { Parameter } from '../../../Providers/components/ParametersProvider';
import { TextSelectorContext } from '../SelectTextProvider';
import { ActiveTabContext } from '../../../Providers/components/ActiveTabProvider';
import { SQLFilesContext } from '../../../Providers/components/SQLFilesProvider';
import DataCloudNoProject from '../SQLEditor/components/DataCloudNoProject';
import { QueryTriggerContext } from '../../../Providers/components/QueryTriggerProvider';
import Timer from './components/Timer';
import QueueAnimation from './components/QueueAnimation';

export type QueryResult = ResultTableType;

export interface QueryError {
  title: string;
  content?: string;
}

interface MainProps {
  file: SQLFile;
  activeKey: string;
}

interface QueryInstance {
  sql?: string;
  isQuerying: boolean;
  result: QueryResult | null;
  error: QueryError | null;
  status: ExecutionStatus | null;
  progress: number;
  queueLength: number | null;
}

const initialQueryInstance: QueryInstance = {
  sql: undefined,
  isQuerying: false,
  result: null,
  error: null,
  status: null,
  progress: 0,
  queueLength: null
};

export class SQLError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SQLError';
  }
}

function extractOrThrow<T>(axiosResponse: AxiosResponse<TaskResponse<T>>): T {
  let errMsg: string;
  if (axiosResponse.status === 200) {
    const taskResponse = axiosResponse.data;
    const { data, message } = taskResponse;
    if (taskResponse.message === 'success') {
      return data;
    }
    errMsg = message;
  } else {
    errMsg = axiosResponse.statusText;
  }
  throw new Error(`Unexpected stauts of ${axiosResponse.status} with message: ${errMsg}`);
}

interface TimerRef {
  intervalId: NodeJS.Timer | null;
  startTime: number;
  count: number;
}

interface CreateButtonProps {
  type: 'task' | 'custom';
  docLink: string;
  children: React.ReactNode
}

function CreateButton({ type, docLink, children } : CreateButtonProps) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col w-[240px] items-center p-0 group">
      {children}
      <h4 className="mt-[16px] text-[16px] text-[var(--v2-color-grey900)] font-medium font-roboto cursor-default">{t(`dataCloud.generate.modal.${type}.name`)}</h4>
      <a
        href={docLink}
        target="_blank"
        rel="noreferrer"
        className="mt-[4px] text-[12px] text-[#000000] opacity-30 leading-tight text-center"
      >
        {t(`dataCloud.generate.modal.${type}.description`)}
      </a>
    </section>
  );
}

function Main({ file, activeKey }: MainProps) {
  const { t } = useTranslation();
  const nav = useNavigate();

  const [queryInstance, setQueryInstance] = useState({ ...initialQueryInstance });
  const textSelector = useContext(TextSelectorContext);
  const activeTabKey = useContext(ActiveTabContext);
  const display = activeKey === activeTabKey.current;

  const resultTable = queryInstance.result;
  const apiGenerator = useContext(APIGeneratorContext);

  const { apiKey } = useApiKey();

  const { useQueryTrigger, request } = useContext(QueryTriggerContext);

  const [abortController, setAbortController] = useState(new AbortController());
  const [executionId, setExecutionId] = useState<string | null>(null);
  const clientRef = useRef(new TaskApi());
  const client = clientRef.current;

  const [timeValue, setTimeValue] = useState(0);
  const timer = useRef<TimerRef>({
    intervalId: null,
    count: 0,
    startTime: 0
  });
  const navigate = useNavigate();
  const { checkWhitelist, openRequestModal } = useWhiteList(FeatureName.custom_api);

  const totalQueueLength = useRef<number | null>(null);

  const startTimer = () => {
    timer.current.startTime = +new Date();
    const intervalId = setInterval(() => {
      timer.current.count = +new Date() - timer.current.startTime;
      setTimeValue(timer.current.count);
    }, 66);
    timer.current.intervalId = intervalId;
  };

  const stopTimer = () => {
    if (timer.current.intervalId) {
      clearInterval(timer.current.intervalId);
      timer.current.intervalId = null;
      timer.current.count = 0;
    }
  };

  const onCaughtError = (error: any) => {
    if (error.message === 'canceled') {
      setQueryInstance({ ...initialQueryInstance });
      return;
    }
    let newError: QueryError;
    if (axios.isAxiosError(error)) {
      newError = {
        title: error.name,
        content: error.message
      };
    } else if (error instanceof SQLError) {
      newError = {
        title: error.name,
        content: error.message
      };
    } else {
      newError = {
        title: '',
        content: JSON.stringify(error)
      };
    }
    setQueryInstance((prev) => {
      if (!prev.isQuerying) {
        return prev;
      }
      return {
        ...prev,
        error: newError,
        isQuerying: false,
        status: null,
        progress: 0
      };
    });
  };

  const executeQuery = async (sql: string, parameters: Parameter[]) => {
    const queryParameters = parameters.map((param) => ({
      key: param.name,
      type: param.dataType,
      value: param?.value !== undefined ? param.value : param.defaultValue
    }));
    const trimedSql = sql.trim();
    const slicedSql = trimedSql.endsWith(';') ? trimedSql.slice(0, trimedSql.length - 1) : trimedSql;
    return client.executeSQLQuery({
      sql: slicedSql,
      parameters: queryParameters
    });
  };

  useQueryTrigger({ event: 'request',
    callback: async (key, sql, parameters) => {
      if (key !== activeKey) {
        return;
      }
      if (queryInstance.isQuerying) {
        return;
      }
      try {
        setQueryInstance({
          ...initialQueryInstance,
          isQuerying: true,
          status: 'PENDING'
        });

        startTimer();
        const executeQueryAxiosResponse = await executeQuery(sql, parameters);
        const {
          executionId: newExecutionId,
          status: initStatus
        } = extractOrThrow(executeQueryAxiosResponse)[0];
        setExecutionId(newExecutionId);
        let status: ExecutionStatus = initStatus;
        let errMsg: string;
        do {
          // eslint-disable-next-line no-await-in-loop
          const executionStatusAxiosResponse = await client.getExecutionStatus(newExecutionId);
          const { status: newStatus, progress, message, queueLength } = extractOrThrow(executionStatusAxiosResponse)[0];
          status = newStatus;
          errMsg = message;
          if (totalQueueLength.current === null) {
            totalQueueLength.current = queueLength;
          }
          if (newStatus !== queryInstance.status || progress !== queryInstance.progress) {
            setQueryInstance((prev) => {
              if (!prev.isQuerying) {
                return prev;
              }
              const newProgress = prev.progress > progress ? prev.progress : progress;
              return {
                ...prev,
                status: newStatus,
                progress: newProgress,
                queueLength
              };
            });

            if (progress === 100) {
              stopTimer();
            }
          }
        } while (!_.includes(['FINISHED', 'FAILED', 'CANCELED'], status));

        if (status !== 'FINISHED') {
          throw new SQLError(errMsg);
        }
        const executionResultAxiosResponse = await client.getExecutionResult(newExecutionId);
        const executionResultData = extractOrThrow(executionResultAxiosResponse);
        const result = castExecutionResultsToQueryResult(executionResultData);
        setQueryInstance((prev) => {
          if (!prev.isQuerying) {
            return prev;
          }
          return {
            ...prev,
            sql,
            result,
            isQuerying: false,
            status: null,
            progress: 0
          };
        });
      } catch (error: any) {
        onCaughtError(error);
      } finally {
        stopTimer();
        totalQueueLength.current = null;
      }
    }
  });

  useQueryTrigger({ event: 'preview',
    callback: async (key, tableName) => {
      if (key !== activeKey) {
        return;
      }
      if (queryInstance.isQuerying) {
        return;
      }
      try {
        setQueryInstance({
          ...initialQueryInstance,
          isQuerying: true,
          status: 'PENDING'
        });

        const axiosResponse = await Request.get(`/v2/datacloud/metadata/${tableName}`);
        const metadata = axiosResponse.data.data;

        if (metadata) {
          setQueryInstance((prev) => {
            if (!prev.isQuerying) {
              return prev;
            }
            return {
              ...prev,
              progress: 100
            };
          });
        }

        const columnNames = metadata.column.map((col: any) => col.name);

        const kvResult = metadata.ExampleData.map((row: any) => {
          const obj: { [key: string]: any } = {};
          columnNames.forEach((name: string, index: number) => {
            const colKey = `${name}_${index}`;
            obj[colKey] = row[index];
          });
          return obj;
        });

        const queryResult = {
          rows: kvResult.length,
          meta: metadata.column.map((value: any, index: number) => ({
            schema_type: 'column',
            name: columnNames[index],
            key: `${value.name}_${index}`,
            type: value.type,
            index
          })),
          kv_result: kvResult
        };

        setQueryInstance((prev) => (prev.isQuerying ? {
          ...prev,
          isQuerying: false,
          result: queryResult,
          status: null,
          progress: 0
        } : prev));
      } catch (error: any) {
        onCaughtError(error);
        totalQueueLength.current = null;
      }
    }
  });

  const handleCancel = () => {
    abortController.abort();
    const newAbortContoller = new AbortController();
    setAbortController(newAbortContoller);
    client.setSignal(newAbortContoller.signal);
    setQueryInstance({ ...initialQueryInstance });
    if (executionId) {
      client.cancelExecution(executionId);
      setExecutionId(null);
    }
  };

  const handleReset = () => {
    setQueryInstance({ ...initialQueryInstance });
  };

  const handleQuery = React.useCallback((): void => {
    if (queryInstance.isQuerying) {
      return;
    }
    if (!file || file.content.length === 0) {
      Toast.error(t('dataCloud.nothingRun'));
      return;
    }

    const { uuid, content, parameters: parametersJsonStr } = file;
    let parameters: Parameter[];
    try {
      parameters = JSON.parse(parametersJsonStr);
    } catch {
      parameters = [];
    }

    const sql = textSelector.text ? textSelector.text : content;

    request(uuid, sql, parameters);
  }, [file, queryInstance.isQuerying, request, t, textSelector.text]);

  if (!display) {
    return null;
  }

  const renderProgress = (percent: number): React.ReactElement => {
    let progressDisplay;
    let displayQueued;
    if (queryInstance.status === null || queryInstance.status === 'PENDING' || queryInstance.status === 'QUEUED') {
      progressDisplay = 'QUEUE...';
      displayQueued = queryInstance;
    } else {
      progressDisplay = `${percent}%`;
    }

    return (
      <>
        {progressDisplay}
        <div className="follow">
          <Timer time={timeValue} />
          {displayQueued && (
          <div className="queue">
            <QueueAnimation key="fixed" />
            {totalQueueLength.current !== null && (
            <div className="text">
              {`Queue..(${queryInstance.queueLength ?? totalQueueLength.current}/${totalQueueLength.current})`}
            </div>
            )}
          </div>
          )}
        </div>
      </>
    );
  };

  const renderContent = (): React.ReactElement => {
    if (queryInstance.isQuerying) {
      return (
        <div>
          <Progress
            className="w-full"
            percent={queryInstance.progress}
            stroke="#3089D2"
            showInfo
            format={renderProgress}
            orbitStroke="#E3E7EC"
          />
        </div>
      );
    }

    if (queryInstance.error !== null) {
      return (
        <div className="p-4 bg-[#FFEBEA]">
          <div className="flex justify-between items-center">
            <h2 className="text-[#890F07] text-[14px] font-semibold">{queryInstance.error.title}</h2>
            <div>
              <button
                type="button"
                className="m-0 p-0"
                onClick={handleReset}
              >
                <Icon className="w-5 h-5" icon="common/close-icon" />
              </button>
            </div>
          </div>
          <div>{queryInstance.error.content}</div>
        </div>
      );
    }

    if (!queryInstance.result) {
      return (<QueryResultEmpty />);
    }

    if (queryInstance.result) {
      return (
        <QueryResultTable
          key={queryInstance.result.task_id}
          resultTable={queryInstance.result}
        />
      );
    }
    return (<div />);
  };

  const renderRunButtonText = (): React.ReactNode => {
    if (queryInstance.isQuerying) {
      return (
        <div className="flex justify-between items-center gap-2">
          <span>
            <Icon icon="common/loading" className="w-3.5 h-3.5 animate-spin" />
          </span>
          <span>
            {`${t('common.running')}...`}
          </span>
        </div>
      );
    }

    if (textSelector.text) {
      return t('common.runSelection');
    }

    return (
      <span className="flex items-center justify-center">
        <div className="flex items-center">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="7.94112" cy="7.99978" rx="7.17647" ry="7.11111" fill="white" />
            <path d="M12 8L6 11.4641L6 4.5359L12 8Z" fill="#191E21" />
          </svg>
        </div>
        <div className="h-full flex items-center ml-[7px]">
          {t('common.run')}
        </div>
        <div className="flex items-center justify-center gap-1 ml-[12px]">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="16" height="16" rx="4" fill="#444D56" />
            <path fillRule="evenodd" clipRule="evenodd" d="M10.2396 4.44063C10.4242 4.36418 10.6273 4.34418 10.8232 4.38315C11.0192 4.42213 11.1991 4.51833 11.3404 4.6596C11.4817 4.80086 11.5779 4.98084 11.6168 5.17679C11.6558 5.37273 11.6358 5.57582 11.5594 5.7604C11.4829 5.94497 11.3534 6.10272 11.1873 6.21371C11.0212 6.32471 10.8259 6.38395 10.6262 6.38395H9.61605V5.37385C9.61605 5.17407 9.67529 4.97877 9.78629 4.81266C9.89728 4.64655 10.055 4.51709 10.2396 4.44063ZM8.80797 6.38395V5.37385C8.80797 5.01424 8.91461 4.66272 9.11439 4.36372C9.31418 4.06472 9.59814 3.83168 9.93037 3.69407C10.2626 3.55645 10.6282 3.52045 10.9809 3.5906C11.3336 3.66076 11.6575 3.83392 11.9118 4.0882C12.1661 4.34248 12.3392 4.66644 12.4094 5.01914C12.4796 5.37183 12.4435 5.7374 12.3059 6.06963C12.1683 6.40186 11.9353 6.68582 11.6363 6.88561C11.3373 7.08539 10.9858 7.19203 10.6262 7.19203H9.61605V8.80819H10.6262C10.9858 8.80819 11.3373 8.91482 11.6363 9.11461C11.9353 9.31439 12.1683 9.59835 12.3059 9.93058C12.4435 10.2628 12.4796 10.6284 12.4094 10.9811C12.3392 11.3338 12.1661 11.6577 11.9118 11.912C11.6575 12.1663 11.3336 12.3395 10.9809 12.4096C10.6282 12.4798 10.2626 12.4438 9.93037 12.3061C9.59814 12.1685 9.31418 11.9355 9.11439 11.6365C8.91461 11.3375 8.80797 10.986 8.80797 10.6264V9.61627H7.19181V10.6264C7.19181 10.986 7.08518 11.3375 6.88539 11.6365C6.68561 11.9355 6.40165 12.1685 6.06942 12.3061C5.73719 12.4438 5.37161 12.4798 5.01892 12.4096C4.66623 12.3395 4.34226 12.1663 4.08798 11.912C3.8337 11.6577 3.66054 11.3338 3.59039 10.9811C3.52023 10.6284 3.55624 10.2628 3.69385 9.93058C3.83146 9.59835 4.0645 9.31439 4.3635 9.11461C4.6625 8.91482 5.01403 8.80819 5.37363 8.80819H6.38373V7.19203H5.37363C5.01403 7.19203 4.6625 7.08539 4.3635 6.88561C4.0645 6.68582 3.83146 6.40186 3.69385 6.06963C3.55624 5.73741 3.52023 5.37183 3.59039 5.01914C3.66054 4.66644 3.8337 4.34248 4.08798 4.0882C4.34226 3.83392 4.66623 3.66076 5.01892 3.5906C5.37161 3.52045 5.73719 3.55645 6.06942 3.69407C6.40165 3.83168 6.68561 4.06472 6.88539 4.36372C7.08518 4.66272 7.19181 5.01424 7.19181 5.37385V6.38395H8.80797ZM8.80797 7.19203V8.80819H7.19181V7.19203H8.80797ZM6.38373 6.38395H5.37363C5.17385 6.38395 4.97856 6.32471 4.81245 6.21371C4.64634 6.10272 4.51687 5.94497 4.44042 5.7604C4.36397 5.57582 4.34396 5.37273 4.38294 5.17679C4.42191 4.98084 4.51812 4.80086 4.65938 4.6596C4.80065 4.51833 4.98063 4.42213 5.17657 4.38315C5.37251 4.34418 5.57561 4.36418 5.76018 4.44063C5.94475 4.51709 6.10251 4.64655 6.2135 4.81266C6.32449 4.97877 6.38373 5.17407 6.38373 5.37385V6.38395ZM6.38373 9.61627V10.6264C6.38373 10.8261 6.32449 11.0214 6.2135 11.1876C6.10251 11.3537 5.94475 11.4831 5.76018 11.5596C5.57561 11.636 5.37251 11.656 5.17657 11.6171C4.98063 11.5781 4.80065 11.4819 4.65938 11.3406C4.51812 11.1994 4.42191 11.0194 4.38294 10.8234C4.34396 10.6275 4.36397 10.4244 4.44042 10.2398C4.51687 10.0552 4.64634 9.89749 4.81245 9.7865C4.97856 9.67551 5.17385 9.61627 5.37363 9.61627H6.38373ZM9.61605 9.61627H10.6262C10.8259 9.61627 11.0212 9.67551 11.1873 9.7865C11.3534 9.89749 11.4829 10.0552 11.5594 10.2398C11.6358 10.4244 11.6558 10.6275 11.6168 10.8234C11.5779 11.0194 11.4817 11.1994 11.3404 11.3406C11.1991 11.4819 11.0192 11.5781 10.8232 11.6171C10.6273 11.656 10.4242 11.636 10.2396 11.5596C10.055 11.4831 9.89728 11.3537 9.78629 11.1876C9.67529 11.0214 9.61605 10.8261 9.61605 10.6264V9.61627Z" fill="#D1D7DD" />
          </svg>

          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="16" height="16" rx="4" fill="#444D56" />
            <path fillRule="evenodd" clipRule="evenodd" d="M12.0001 7.55545V4.44434H11.1112V7.55545C11.1112 8.29183 10.5143 8.88878 9.77791 8.88878H7.11125L7.11125 7.11035L4.44458 9.33257L7.11125 11.5548L7.11125 9.77767H9.77791C11.0052 9.77767 12.0001 8.78275 12.0001 7.55545Z" fill="#D1D7DD" />
          </svg>

        </div>
      </span>
    );
  };

  const handleOpenGenerateAPIModal = () => {
    if (!apiKey) {
      const modal = Modal.info({
        className: 'datacloud-no-projcet',
        title: null,
        footer: null,
        content: <DataCloudNoProject onClick={() => {
          nav('/');
          modal.destroy();
        }}
        />,
        height: 400
      });
      return;
    }
    if (file?.queryId) {
      apiGenerator.open();
    } else {
      Toast.info(t('dataCloud.generate.unSave'));
    }
  };

  const openGenerateAPIModal = () => {
    const modal = Modal.info({
      icon: null,
      size: 'large',
      title: t('dataCloud.generate.modal.title'),
      style: {
        width: '600px'
      },
      centered: true,
      content: (
        <Form
          initValues={{ generationApiType: 'task' }}
          onSubmit={(values) => {
            if (values.generationApiType === 'custom') {
              if (!queryInstance.sql || !queryInstance.result?.meta) {
                Toast.info(t('dataCloud.generate.successfulExecutionPrompt'));
                modal.destroy();
                return;
              }

              if (!checkWhitelist()) {
                openRequestModal();
                modal.destroy();
                return;
              }

              navigate('/dataCloud/api/custom', {
                state: {
                  sql: queryInstance.sql,
                  columns: queryInstance.result?.meta?.map((column: ColumnSchema) => ({
                    name: column.name,
                    type: column.type
                  }))
                }
              });
              modal.destroy();
            } else {
              handleOpenGenerateAPIModal();
              modal.destroy();
            }
          }}
        >
          <Form.RadioGroup
            field="generationApiType"
            noLabel
            rules={[
              { type: 'string' },
              { required: true }
            ]}
            defaultValue="task"
            type="pureCard"
          >
            <Row type="flex" justify="space-around" className="mt-[36px] mb-[16px]">
              <CreateButton
                type="custom"
                docLink="https://docs.chainbase.com/reference/data-cloud-custom-api"
              >
                <Form.Radio
                  value="custom"
                  extra={(
                    <div className="flex-shrink-0 flex items-center justify-center w-[140px] h-[140px] rounded-[4px] bg-[var(--v2-color-grey50)] border-[1px] border-solid border-[var(--v2-color-grey300)] group-hover:bg-[var(--v2-color-grey100)] transition-all cursor-pointer">
                      <Icon className="w-[63px] h-[63px]" icon="api/custom" />
                    </div>
                  )}
                />
              </CreateButton>

              <CreateButton
                type="task"
                docLink="https://docs.chainbase.com/reference/data-cloud-studio-api"
              >
                <Form.Radio
                  value="task"
                  extra={(
                    <div className="flex-shrink-0 flex items-center justify-center w-[140px] h-[140px] rounded-[4px] bg-[var(--v2-color-grey50)] border-[1px] border-solid border-[var(--v2-color-grey300)] group-hover:bg-[var(--v2-color-grey100)] transition-all cursor-pointer">
                      <Icon className="w-[63px] h-[63px]" icon="api/task" />
                    </div>
                  )}
                />
              </CreateButton>
            </Row>
          </Form.RadioGroup>
          <Button
            size="large"
            icon={<Icon icon="sync/jobs/overview/add" />}
            theme="solid"
            type="primary"
            htmlType="submit"
            className="mx-auto mt-4"
          >
            {t('common.create')}
          </Button>
        </Form>
      ),
      closeIcon: <Icon className="icon icon-close" icon="modal/close" />,
      footer: null,
      onCancel: () => {
        modal.destroy();
      }
    });
  };

  const renderHeader = (): React.ReactElement => (
    <div className={`query-result-header-v2 ${queryInstance.isQuerying ? '' : 'border-solid border-t-0 border-x-0 border-[4px] border-[#E3E7EC]'}`}>
      <Row type="flex" align="middle">
        <h3 className="font-semibold">{t('dataCloud.queryResult')}</h3>
        {
      resultTable && !resultTable?.err_msg
        ? (
          <QueryStatus
            rows={resultTable?.rows}
            size={resultTable?.bytes_read}
            latency={resultTable?.elapsed}
            onDownload={() => {
              ExcelUtils.exportCSVWithKVResult(`result-${resultTable.task_id}`, resultTable.meta, resultTable.kv_result);
            }}
          />
        ) : null
    }
      </Row>

      <div className="operate-group">
        {queryInstance.isQuerying ? (
          <Button
            onClick={handleCancel}
            theme="borderless"
            className="btn-cancel"
            htmlType="button"
          >
            {t('common.cancel')}
          </Button>
        ) : (
          <Button
            icon={<Icon className="icon" icon="dataCloud/generate" />}
            className="btn-generate"
            onClick={openGenerateAPIModal}
          >
            {t('dataCloud.generate.title')}
          </Button>
        )}

        <Button
          onClick={handleQuery}
          theme="solid"
          className={classNames('btn-run', {
            running: queryInstance.isQuerying
          })}
          type="primary"
          htmlType="button"
        >
          {renderRunButtonText()}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="query-result-content">
      {renderHeader()}
      <div className="query-result-body">
        {renderContent()}
      </div>
    </div>
  );
}

export default function ResultViewer(): React.ReactElement {
  const sqlFiles = useContext(SQLFilesContext);

  if (!sqlFiles) {
    return <BlockLoading />;
  }

  return (
    <>
      {sqlFiles.map((file) => (
        <Main
          key={file.uuid}
          activeKey={file.uuid}
          file={file}
        />
      ))}
    </>
  );
}
